
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { useTenant } from "@/contexts/TenantContext"
import { useLocation } from "react-router-dom"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-[#69727d] text-white hover:bg-[#5f6974] border-2 border-transparent",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const { tenant } = useTenant();
    const location = useLocation();
    const urlMatch = location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
    const subFromUrl = urlMatch ? urlMatch[1] : undefined;
    const subdomain = tenant?.subdomain || subFromUrl;
    const isAudits = subdomain === 'audits';
    const isDCC = subdomain === 'dubaicommercity';
    const isDefault = (variant ?? 'default') === 'default';

    // Apply tenant-specific colors for default variant
    let tenantOverride = "";
    if (isAudits && isDefault) {
      // Special case for audits tenant
      tenantOverride = "bg-[#D9FF3F] text-[#0A1A2B] hover:bg-[#D9FF3F]/90";
    }

    // DCC: Global CTA color override for default buttons
    const dccOverride = isDCC && isDefault
      ? "bg-[#4B1F5E] text-white hover:bg-[#3D184C] border-2 border-transparent"
      : "";

    return (
      <Comp
        className={cn(tenantOverride, dccOverride, buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
